import { type ArchiveDay, type ArchiveItem, formatDuration, type Lookup, type Relaxation } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';
import type { MouseEvent, ReactElement, ReactNode } from 'react';

import { dailyPath, type PlayContext } from '../../app/play-context.ts';
import { Link } from '../../app/router.tsx';
import { Icon, Wedge } from '../../components/icons.tsx';
import { Nib } from '../../components/Nib.tsx';
import { LEVEL_NAME } from '../today-model.ts';
import { Cover } from './Cover.tsx';
import { dayMonthWords, itemLabel, itemMeta, itemTitle, longDate, plural, shortDate } from './model.ts';

/** Play's pieces: the state line, a row, a stack's tile, a day found by number or date, and no-results. */

const TICK = (
	<svg viewBox="0 0 16 16" aria-hidden="true">
		<path d="M3.5 8.5l3 3 6-7" />
	</svg>
);

export const PLAY_TRIANGLE = (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="M7 4.5v15L19.5 12z" />
	</svg>
);

/**
 * The state as a glyph and words, never colour alone (the row's label says it in full). In a
 * list that is all missed days (`missedGiven`), "● Missed" would only repeat the list's name
 * on every row, so a missed day shows the plain ›, as an unplayed one does (QA1-5): the whole
 * row is the link, and "Play" on every row said nothing the arrow doesn't.
 */
export function StateLine({ item, quiet = '', missedGiven = false }: { item: ArchiveItem; quiet?: string; missedGiven?: boolean }) {
	if (item.status === 'done') {
		const late = item.onTime === false;
		return (
			<span className="st">
				<span className={`tick${late ? ' late' : ''}${item.perfect ? ' pf' : ''}`}>{TICK}</span>
				{item.perfect ? 'Perfect ' : ''}
				{formatDuration(item.elapsedMs)}
				{late && <span className="st-w">&nbsp;caught up</span>}
			</span>
		);
	}
	if (item.status === 'started') {
		const pct = item.pct === null ? null : Math.round(item.pct * 100);
		return (
			<span className="st">
				<span className="pbar" aria-hidden="true">
					<i style={{ width: `${pct ?? 8}%` }} />
				</span>
				{pct === null ? 'Started' : `${pct}%`}
				<span className="st-w">&nbsp;· {formatDuration(item.elapsedMs)}</span>
			</span>
		);
	}
	if (item.missed && !missedGiven) {
		return (
			<span className="st">
				<span className="mdot" aria-hidden="true" />
				Missed
			</span>
		);
	}
	return (
		<span className="st q">
			{quiet}
			<Wedge direction="right" className="st-wg" />
		</span>
	);
}

/** One puzzle in a list: cover · "No. n · date" · level and size · state. */
export function Row({
	item,
	to,
	onOpen,
	missedGiven = false,
	levelFirst = false,
}: {
	item: ArchiveItem;
	to: string;
	onOpen?: () => void;
	missedGiven?: boolean;
	/** Lead with the level ("Easy"), the day after: a special's puzzles can share one day. */
	levelFirst?: boolean;
}) {
	return (
		<li>
			<Link to={to} className="row" aria-label={itemLabel(item)} onNavigate={onOpen}>
				<Cover shape={item.shape} className="row-cv" />
				<span className="row-mid" aria-hidden="true">
					<span className="row-no">{levelFirst ? LEVEL_NAME[item.level] : itemTitle(item)}</span>
					<span className="row-meta">{levelFirst ? `${item.width}×${item.height} · ${itemTitle(item)}` : itemMeta(item)}</span>
				</span>
				<span className="row-r" aria-hidden="true">
					<StateLine item={item} missedGiven={missedGiven} />
				</span>
			</Link>
		</li>
	);
}

/** A list, a page at a time ("Show 40 more"), so a long stack stays light. */
export function Rows({
	items,
	shown,
	onMore,
	path,
	onOpen,
	label,
	missedGiven = false,
	levelFirst = false,
}: {
	items: readonly ArchiveItem[];
	shown: number;
	onMore: () => void;
	path: (item: ArchiveItem) => string;
	onOpen?: (item: ArchiveItem, index: number) => void;
	label: string;
	/** Every row is a missed day (the Missed collection, a "missed" search): don't mark each one. */
	missedGiven?: boolean;
	/** Each row leads with its level (a special's rows: see {@link Row}). */
	levelFirst?: boolean;
}) {
	const rest = items.length - shown;
	return (
		<>
			<ul className="rows" aria-label={label}>
				{items.slice(0, shown).map((it, i) => (
					<Row key={it.key} item={it} to={path(it)} onOpen={onOpen ? () => onOpen(it, i) : undefined} missedGiven={missedGiven} levelFirst={levelFirst} />
				))}
			</ul>
			{rest > 0 && (
				<button type="button" className="btn sm secondary more-btn" onClick={onMore}>
					Show {Math.min(rest, PAGE)} more
					{/* "of 23" only when there's more than one page left (not "Show 23 more of 23"). */}
					{rest > PAGE && <span className="more-of"> of {rest}</span>}
				</button>
			)}
		</>
	);
}

/** Rows shown at a time. */
export const PAGE = 40;

/**
 * A stack: its name, a line, its top three covers fanned, how many it holds ("12 puzzles"),
 * and ▶ to play the top one, a quiet stock disc.
 * An empty one goes quiet: an outline, its name, "None yet". The whole tile opens it.
 */
export function StackTile({
	id,
	mine,
	name,
	sub,
	count,
	top,
	fan,
	href,
	onOpen,
	onPlay,
}: {
	/** The open link's id. */
	id: string;
	mine: boolean;
	name: string;
	sub: string;
	count: number;
	top: ArchiveItem | null;
	fan: readonly ArchiveItem[];
	href: string;
	onOpen: () => void;
	onPlay: () => void;
}) {
	const empty = count === 0;
	const open = (e: MouseEvent<HTMLAnchorElement>) => {
		if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		onOpen();
	};
	return (
		<li className={`stk${empty ? ' none' : ''}${mine ? ' mine' : ''}`}>
			<h3 className="stk-name">
				<a id={id} href={href} onClick={open} aria-label={`${name}${mine ? ', yours' : ''}, ${count} ${plural(count, 'puzzle')}: open the collection`}>
					{name}
				</a>
			</h3>
			{!empty && sub && <p className="stk-sub">{sub}</p>}
			<p className="stk-ct" aria-hidden="true">
				{empty ? 'None yet' : `${count} ${plural(count, 'puzzle')}`}
			</p>
			{!empty && (
				<span className="stk-fan" aria-hidden="true">
					{[...fan].reverse().map((it) => (
						<Cover key={it.key} shape={it.shape} />
					))}
				</span>
			)}
			{top && (
				<button type="button" className="btn round stk-go secondary" onClick={onPlay} aria-label={`Play the top one in ${name}: ${itemLabel(top)}`}>
					{PLAY_TRIANGLE}
				</button>
			)}
		</li>
	);
}

/** The last tile: make a stack of your own (or, with 12, a note that that's the most). */
export function NewStackTile({ full, onOpen, trigger }: { full: boolean; onOpen: () => void; trigger: (button: ReactElement) => ReactNode }) {
	return (
		<li className="stk add">
			{full ? (
				<p className="stk-sub">You can keep 12 collections. Delete one to make another.</p>
			) : (
				<>
					<span className="stk-plus" aria-hidden="true">
						<Icon.plus />
					</span>
					<h3 className="stk-name">
						{/* The builder's Radix trigger (Play's useSheetOpeners). */}
						{trigger(
							<button type="button" onClick={onOpen}>
								New collection
							</button>,
						)}
					</h3>
				</>
			)}
		</li>
	);
}

/** The answer to a number or a date: that day and its levels, Today, or "not out yet" (never a hint of what). */
export function JumpCard({
	lookup,
	asked,
	items,
	todayNumber,
	today,
	ctx,
	path,
	onOpen,
}: {
	lookup: Lookup;
	/** What was asked, in words: "No. 30", "30 October". */
	asked: string;
	items: readonly ArchiveItem[];
	/** Today's number, for "isn't out yet" (Play lists only past days, so not its newest). */
	todayNumber: number | null;
	/** Today's puzzle: the level it opens at (the one last played, else the next not solved) and what the button says. */
	today: { level: Level; verb: 'Play' | 'Continue' | 'See result' };
	/** How the lookup was reached, so "‹ Play" comes back to it. */
	ctx: PlayContext;
	path: (item: ArchiveItem) => string;
	onOpen: (item: ArchiveItem) => void;
}) {
	if (lookup.kind !== 'day') {
		let head: ReactNode;
		let body: ReactNode;
		if (lookup.kind === 'today') {
			// Straight into today's game at the level last played, not round by Today (UX-059);
			// once that one's solved, the next one that isn't, or the result when all are (QA2-14).
			if (lookup.number) {
				const label = today.verb === 'See result' ? 'See result' : `${today.verb} ${LEVEL_NAME[today.level]}`;
				return (
					<div className="jump note today">
						<p>
							<b>No.&nbsp;{lookup.number} is today’s</b>
						</p>
						<Link
							to={dailyPath(lookup.number, today.level, ctx)}
							className={`btn sm ${today.verb === 'See result' ? 'secondary' : 'primary'}`}
							aria-label={today.verb === 'See result' ? `See result: ${LEVEL_NAME[today.level]}` : undefined}
						>
							{label}
							{today.verb !== 'See result' && <Wedge direction="right" />}
						</Link>
					</div>
				);
			}
			head = <>That’s today’s</>;
			body = (
				<>
					Play it on{' '}
					<Link to="/" how="up" className="tlink">
						Today
					</Link>
					.
				</>
			);
		} else if (lookup.kind === 'future') {
			head = <>{asked} isn’t out yet</>;
			body = todayNumber ? <>Today’s is No.&nbsp;{todayNumber}.</> : null;
		} else if (lookup.kind === 'before') {
			// No. 1's date from the archive itself (it moved once already, in the numbering reset).
			const first = items.find((it) => it.number === 1 && it.date);
			head = <>Sparrow starts at No.&nbsp;1</>;
			body = first?.date ? <>That was {longDate(first.date)}.</> : null;
		} else {
			head = <>Couldn’t find {asked}</>;
			body = <>Try another number or date.</>;
		}
		return (
			<div className="jump note">
				<p>
					<b>{head}</b>
					{body}
				</p>
			</div>
		);
	}
	const day: ArchiveDay = lookup.day;
	const dayItems = items.filter((it) => it.date === day.date);
	return (
		<section className="jump" aria-label={`No. ${day.number}, ${longDate(day.date)}`}>
			<div className="jh">
				<b>
					No.&nbsp;{day.number} · {shortDate(day.date)}
				</b>
				<small>
					{dayItems.length} {plural(dayItems.length, 'level')}
				</small>
			</div>
			<ul className="dlvs">
				{dayItems.map((it) => {
					const done = it.status === 'done';
					const verb = done ? 'See result' : it.status === 'started' ? 'Continue' : 'Play';
					return (
						<li key={it.key} className={`dlv${lookup.level === it.level ? ' focus' : ''}`}>
							<Cover shape={it.shape} className="dlv-cv" />
							<span className="dlv-mid">
								<span className="dlv-lv">
									{LEVEL_NAME[it.level]}
									<small>
										{it.width}×{it.height}
									</small>
								</span>
								<StateLine item={it} quiet="Not started" />
							</span>
							<Link
								to={path(it)}
								className={`btn sm ${done ? 'secondary' : 'primary'}`}
								aria-label={`${verb}: ${itemLabel(it)}`}
								onNavigate={() => onOpen(it)}
							>
								{verb}
							</Link>
						</li>
					);
				})}
			</ul>
		</section>
	);
}

/**
 * Nothing matched: say what was asked, offer the nearest loosenings with what each adds, and
 * a quiet way back to the collections.
 */
export function NoResults({
	what,
	relax,
	label,
	onRelax,
	onClear,
}: {
	what: string;
	relax: readonly Relaxation[];
	label: (r: Relaxation) => string;
	onRelax: (r: Relaxation) => void;
	onClear: () => void;
}) {
	return (
		<div className="nores">
			<Nib pose="ruffled" className="empty-nib" />
			<p className="empty-line">No {what}.</p>
			{relax.length > 0 && (
				<div className="relax">
					{relax.map((r) => (
						<button key={r.key} type="button" className="btn sm secondary" onClick={() => onRelax(r)} aria-label={`${label(r)}: ${r.adds} more ${plural(r.adds, 'puzzle')}`}>
							{label(r)}
							<small>+{r.adds}</small>
						</button>
					))}
				</div>
			)}
			<button type="button" className="tlink" onClick={onClear}>
				Clear the search
			</button>
		</div>
	);
}

/** Words the search tries when it understood none of yours. */
const TRY_WORDS = ['hard', 'Sunday', 'October'] as const;

/**
 * A search with no word Sparrow understands ("fire"): no list, since every puzzle would look
 * like an answer, only what it skipped and a few words to try, each filling the box (UX-055).
 */
export function NotUnderstood({ words, onTry }: { words: string; onTry: (word: string) => void }) {
	return (
		<div className="nores">
			<Nib pose="ruffled" className="empty-nib" />
			<p className="empty-line">Nothing found for “{words}”.</p>
			<p className="empty-sub">Try a level, a day, a month or a date.</p>
			<div className="relax">
				{TRY_WORDS.map((w) => (
					<button key={w} type="button" className="btn sm secondary" onClick={() => onTry(w)}>
						{w}
					</button>
				))}
			</div>
		</div>
	);
}

/** "No. 30" or "30 October": a lookup, said back. */
export function askedWords(q: { kind: 'number'; number: number } | { kind: 'date'; date: string }): string {
	return q.kind === 'number' ? `No. ${q.number}` : dayMonthWords(q.date);
}
